import React, { useEffect, useState } from 'react';
import PreferencesSection from '../PreferencesSection';
import { SFEntityID } from 'utils/types';
import useStarfishAPI from 'hooks/useStarfishAPI';
import { urlParams } from 'utils/request';
import MyLocationRow from './MyLocationRow';
import css from './MyLocations.scss';
import { Table } from 'semantic-ui-react';
import ConfirmDialog from 'common/ConfirmDialog';
import AddLocationModal from './AddLocationModal';
import PropTypes from 'prop-types';
import Icon from 'SF/MaterialIcon';
import Toast from 'common/Toast';

const MyLocations = ({ isIE, userId }) => {
    const [ locations, setLocations ] = useState([]);
    const [ showError, setShowError ] = useState(false);
    const [ errorMessage, setErrorMessage ] = useState('');
    const [ locationTypes, setLocationTypes ] = useState([]);
    const [ locationToEdit, setLocationToEdit ] = useState(null);
    const [ isModalOpen, setModalOpen ] = useState(false);
    const [ locationsResponse, callLocationsAPI ] = useStarfishAPI();
    const [ locationTypesResponse, callLocationTypesAPI ] = useStarfishAPI();
    const [ deleteResponse, callDeleteAPI ] = useStarfishAPI();
    const [ showToast, setShowToast ] = useState(false);
    const [ toastMessage, setToastMessage] = useState(null);
    
    const loadLocations = () =>
        callLocationsAPI('../instructor/getLocationPreferences.html', {
            method: 'POST',
            body: urlParams({ instructorId: userId })
        });

    useEffect(() => {
        loadLocations();
        callLocationTypesAPI('../instructor/getLocationTypes.html');
    }, [userId]);
    
    useEffect(() => {
        if (locationsResponse.success && locationsResponse.data.locations)
            setLocations(locationsResponse.data.locations);
    }, [locationsResponse]);
    
    useEffect(() => {
        if (locationTypesResponse.success) 
            setLocationTypes(locationTypesResponse.data.result.map(type => {
                return { label: type.name, value: type.key }
            }));
    }, [locationTypesResponse]);
    
    const deleteLocation = async (location) =>
        await callDeleteAPI('../instructor/deleteLocationPreferences.html', {
            method: 'POST',
            body: urlParams(location)
        });
    
    useEffect(() => {
        if (deleteResponse.success) {
            setToastMessage("Location deleted successfully.");
            setShowToast(true);
            loadLocations();
        } else if (deleteResponse.error) {
            setErrorMessage(deleteResponse.data.message);
            setShowError(true);
        }
    }, [deleteResponse]);
    
    const closeAddModal = () => {
        setModalOpen(false);
        setLocationToEdit(null); // clear out location being edited
    };
    
    const getLocationRows = () =>
        locations.map((location, index) => 
            <MyLocationRow deleteLocation={deleteLocation}
                           editLocation={() => {
                               setLocationToEdit(location);
                               setModalOpen(true);
                           }}
                           location={location}
                           index={index}
                           key={index} />);
    
    const getLocationTable = () => {
        if (locations.length === 0) return <div className={css['no-locations']}>No locations added</div>;
        return (
            <Table unstackable
                   className={css['table']}
                   data-testid="myLocationsTable"
                   role="grid">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell role="columnheader" scope="col">Name</Table.HeaderCell>
                        <Table.HeaderCell role="columnheader" scope="col">Type</Table.HeaderCell>
                        <Table.HeaderCell role="columnheader" scope="col" />
                    </Table.Row>
                </Table.Header>
                {getLocationRows()}
            </Table>
        );
    };
    
    const getContent = () => {
        if (!locationsResponse.success) return <div>Loading...</div>;
        return (
            <div>
                <button className={css['add-btn']}
                        onClick={() => setModalOpen(true)}>
                    <Icon icon="add_circle" aria-hidden={true} />Add Location
                </button>
                {getLocationTable()}
                <ConfirmDialog headerText="Location cannot be deleted."
                               className={css['delete-error']}
                               descriptionText={errorMessage}
                               onConfirm={() => setShowError(false)}
                               confirmText="OK"
                               isOpen={showError} />
                <AddLocationModal closeModal={closeAddModal}
                                  isEdit={locationToEdit !== null}
                                  isIE={isIE}
                                  isOpen={isModalOpen}
                                  location={locationToEdit}
                                  locationTypes={locationTypes}
                                  onSave={() => {
                                      setToastMessage(`Location ${locationToEdit === null ? 'added' : 'edited'} successfully.`);
                                      setShowToast(true);
                                      closeAddModal();
                                      loadLocations();
                                  }} />
                {showToast && <Toast message={toastMessage} onTimeout={() => setShowToast(false)} />}
            </div>
        )
    };
    
    return (
        <PreferencesSection isStaff
                            headerText="My Locations"
                            descriptionText="Customize your appointment locations.">
            {getContent()}
        </PreferencesSection>
    );
};

MyLocations.propTypes = {
    isIE: PropTypes.bool,
    userId: SFEntityID.isRequired
};

export default MyLocations;
